/** @jsxImportSource ./simboluri-jsx */
// Acasă — harta capitolelor (mock-ui/design/StrCapitole.dc.html): cei 19 pași
// de calcul, grupați în cele 6 capitole de CITIT. Home-ul e capitolul 6
// (Soluția): întrebarea cu care intri în proiect e „ce mai pică", nu „ce
// scrie în studiul geotehnic".

import { CAPITOLE, Capitol } from "./capitole";
import { IcBucla, IcStea } from "./icoane";
import { hashSectiune } from "./legaturi";
import { StepState } from "./tipuri";
import { numar, t, useT, useTx } from "../i18n";
import { categoriiDinStare, explicaPicatele } from "../picate";

// starea agregată a unui capitol, din stările pașilor lui: un pas picat ⇒
// bad, unul lipsă/în lucru ⇒ amb, toate ok ⇒ ok
function stareCapitol(cap: Capitol, steps: Record<string, StepState>): string {
  let are = 0;
  for (const k of cap.pasi) {
    const st = steps[k];
    if (st?.status === "error") return "bad";
    if (st?.result) are += 1;
  }
  if (are === 0) return "";
  return are === cap.pasi.length ? "ok" : "amb";
}

function nrRo(v: number, zecimale = 0): string {
  return numar(v, {
    minimumFractionDigits: zecimale,
    maximumFractionDigits: zecimale,
  });
}

// Cifra-titlu a fiecărui capitol, citită DEFENSIV din rezultatele pașilor:
// rezultatele pot lipsi (pas nerulat) sau pot fi ale unei versiuni mai vechi.
function cifraCapitol(
  cap: Capitol,
  steps: Record<string, StepState>
): { nr: string; um: string; titlu?: string } | null {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  if (cap.nr === 3) {
    const r = steps["model_fem"]?.result as any;
    const vm = r?.verificare_modala;
    const moduri = vm?.moduri as { mod: number; T_s: number }[] | undefined;
    if (moduri?.length) {
      const fundamental = vm?.mod_fundamental ?? moduri[0].mod;
      const m = moduri.find((x) => x.mod === fundamental) ?? moduri[0];
      return { nr: nrRo(m.T_s, 3), um: t("struct.acasa.um_t1") };
    }
    return null;
  }
  if (cap.nr === 6) {
    const r = steps["optimizare_structura"]?.result as any;
    const stare = r?.raport_solutie?.final ?? r?.stare_finala;
    if (stare?.nr_picate != null) {
      const cost = stare?.cost?.total_eur ?? stare?.cost?.total_direct_eur;
      const um = cost
        ? t("struct.acasa.um_picate_cost", { cost: nrRo(cost / 1000) })
        : t("struct.acasa.um_picate");
      // la hover, CARE pică: cifra singură spune doar că e rău
      return { nr: nrRo(stare.nr_picate), um,
               titlu: explicaPicatele(stare.nr_picate, categoriiDinStare(stare)) };
    }
    return null;
  }
  if (cap.nr === 5) {
    // picatele capitolului „Elementul", din tabloul de acoperire al P17
    const r = steps["optimizare_structura"]?.result as any;
    const acoperire = (r?.raport_solutie?.final ?? r?.stare_finala)
      ?.acoperire as { pas: string; nr_verificari: number; nr_picate?: number }[] | undefined;
    if (acoperire?.length) {
      const ale = acoperire.filter((a) => cap.pasi.includes(a.pas));
      if (ale.length) {
        const picate = ale.reduce((s, a) => s + (a.nr_picate ?? 0), 0);
        const total = ale.reduce((s, a) => s + (a.nr_verificari ?? 0), 0);
        return { nr: nrRo(picate), um: t("struct.acasa.um_picate_din", { total: nrRo(total) }) };
      }
    }
    return null;
  }
  /* eslint-enable @typescript-eslint/no-explicit-any */
  return null;
}

export default function Acasa({
  id,
  solutie,
  steps,
}: {
  id: string;
  solutie?: string | null;
  steps: Record<string, StepState>;
}) {
  const t = useT();
  const tx = useTx();
  return (
    <div className="ob-pagina">
      <div className="ob-acasa-head">
        <h2>{t("struct.acasa.titlu")}</h2>
        <span className="ob-chip ob-c-acc">
          <IcStea />
          {t("struct.acasa.chip")}
        </span>
        <span className="ob-acasa-sub">
          {t("struct.acasa.sub")}
        </span>
        <span style={{ flex: 1 }} />
        <a href={hashSectiune(id, "pipeline", solutie ?? null)} style={{ fontSize: 12 }}>
          {t("struct.acasa.vezi_pipeline")}
        </a>
      </div>

      <div className="ob-cap-grila">
        {CAPITOLE.map((cap) => {
          const stare = stareCapitol(cap, steps);
          const cifra = cifraCapitol(cap, steps);
          return (
            <a
              key={cap.nr}
              className={`ob-card ob-cap-card${stare ? ` ob-stare-${stare}` : ""}`}
              // capitolele 4 și 5 au paginile LOR (fundația pe axe, lista
              // elementelor); celelalte se deschid pe PRIMUL pas al capitolului
              href={hashSectiune(
                id,
                cap.nr === 5
                  ? "elemente"
                  : cap.nr === 4
                    ? "fundatie"
                    : cap.pasi[0],
                solutie ?? null
              )}
            >
              <div className="ob-cap-titlu">
                <span className="ob-lbl" style={{ fontSize: 10 }}>{t("struct.acasa.cap_nr", { nr: cap.nr })}</span>
                <b>{t(cap.titlu)}</b>
              </div>
              {cifra ? (
                <div className="ob-cap-cifra" title={cifra.titlu}>
                  <span className="nr ob-disp">{cifra.nr}</span>{" "}
                  <span className="um">{cifra.um}</span>
                </div>
              ) : (
                <div className="ob-cap-cifra">
                  <span className="um" style={{ color: "var(--mut)" }}>
                    {stare === ""
                      ? t("struct.acasa.nerulat")
                      : stare === "amb"
                        ? t("struct.acasa.partial")
                        : " "}
                  </span>
                </div>
              )}
              <div className="ob-cap-gran">{t(cap.granularitate)}</div>
              {(
                <div className="ob-cap-refs">
                  {cap.refs.map((ref) => {
                    const st = steps[ref.key];
                    const cls = st?.status === "error" ? " ob-c-bad" : "";
                    return (
                      // link imbricat în card: navigarea pe pas, nu pe capitol
                      <span
                        key={ref.key}
                        className={`ob-ref${cls}`}
                        role="link"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          window.location.href = hashSectiune(id, ref.key, solutie ?? null);
                        }}
                        style={{ fontSize: 10, cursor: "pointer" }}
                      >
                        {t(ref.eticheta)}
                      </span>
                    );
                  })}
                </div>
              )}
            </a>
          );
        })}
      </div>

      <div className="ob-card ob-bucla">
        <IcBucla />
        <div>
          <b>{t("struct.acasa.bucla_titlu")}</b>{" "}
          <span>{tx("struct.acasa.bucla")}</span>
        </div>
      </div>
    </div>
  );
}
